import { create, type StateCreator } from 'zustand';
import { persist, type PersistOptions } from 'zustand/middleware';
import type { GamePhase, Effect, ConnectionMetrics, GameCatalogEntry } from '@multipack/protocol';
import { ConnectionMonitor } from '@multipack/protocol';
import { createBrowserLogger, setLogSocket } from '@multipack/logger/browser';
import { AVATAR_TYPES, getRandomAvatar } from '../components/ProfilePicture';
import {
  PeerConnection,
  parseIceServers,
  type ConnectionState,
  type DataChannelMessage,
} from '@multipack/webrtc';
import { dispatchEffect } from '../components/EffectProvider';

// Extend Window to include optional config property
declare global {
  interface Window {
    __MULTIPACK_WS_URL__?: string;
    __ZUSTAND_PLAYER_STORE__?: {
      getState: () => PlayerState;
      setState: (state: Partial<PlayerState>) => void;
      subscribe: (listener: (state: PlayerState) => void) => () => void;
    };
  }
}

// Extended WebSocket with custom properties for heartbeat cleanup
interface ExtendedWebSocket extends WebSocket {
  __heartbeatCleanup?: () => void;
}

// Detect if we're in test harness mode (autoJoin in URL hash)
// In this mode, skip localStorage to avoid conflicts between iframes
const isTestHarnessMode =
  typeof window !== 'undefined' && window.location.hash.includes(':autoJoin');

// Log test harness detection for debugging
if (typeof window !== 'undefined') {
  console.log('[playerStore] isTestHarnessMode:', isTestHarnessMode, 'hash:', window.location.hash);
}

// In test harness mode, clear any existing session data immediately
// This ensures each iframe starts fresh without inheriting sessions
if (isTestHarnessMode && typeof window !== 'undefined') {
  console.log('[playerStore] Clearing localStorage for test harness mode');
  try {
    localStorage.removeItem('multipack-player');
  } catch {
    // Ignore errors (e.g., localStorage not available)
  }
}

/** Player info for display purposes */
interface PlayerInfo {
  id: string;
  name: string;
  avatar?: string;
  connectionStatus: 'connected' | 'disconnected' | 'reconnecting';
  score: number;
}

// Export logger for use in other components
export const logger = createBrowserLogger({
  // Default to 'trace' (maximum verbosity) for debugging
  level: 'trace',
  component: 'controller',
});

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

interface OwnerInfo {
  id: string;
  name: string;
}

interface GameInfo {
  gameType: string;
  gameName: string;
  minPlayers: number;
  maxPlayers: number;
  displayColor?: string;
  icon?: string;
}

/** Message buffered during reconnection */
interface PendingMessage {
  message: { type: string; action: string; data: unknown; sequence: number };
  timestamp: number;
}

type AvatarUpdateSource = 'manual' | 'auto';

/** Reaction info from other players */
export interface ReactionInfo {
  playerId: string;
  playerName: string;
  reaction: string;
  timestamp: number;
}

// Maximum age for pending messages (90 seconds)
// Must be longer than max possible reconnection time with exponential backoff
// (RECONNECT_INITIAL_DELAY * sum of 2^0 to 2^max_attempts, capped at RECONNECT_MAX_DELAY)
const PENDING_MESSAGE_TTL = 90000;

// Join retry configuration
const JOIN_MAX_RETRIES = 3;
const JOIN_RETRY_BASE_DELAY = 1000; // 1s, 2s, 4s with exponential backoff

// Errors that should not trigger a retry (definitive failures)
const NON_RETRYABLE_ERRORS = [
  'INVALID_CODE',
  'ROOM_CLOSED',
  'ROOM_FULL',
  'NAME_TAKEN',
  'NAME_TOO_LONG',
  'NAME_REQUIRED',
  'NAME_INVALID_CHARS',
  'GAME_IN_PROGRESS',
];

interface PlayerState {
  // Connection
  connectionStatus: ConnectionStatus;
  socket: ExtendedWebSocket | null;

  // Session
  sessionToken: string | null;
  playerId: string | null;
  playerName: string | null;
  playerAvatar: string | null;
  isAvatarChosen: boolean;

  // Room
  roomCode: string | null;
  roomId: string | null;
  hostName: string | null;
  phase: GamePhase;
  players: PlayerInfo[];
  spectators: PlayerInfo[];

  // Owner/role
  isOwner: boolean;
  isSpectator: boolean;
  owner: OwnerInfo | null;

  // Game
  gameType: string | null;
  gameInfo: GameInfo | null;
  gameCatalog: GameCatalogEntry[];
  privateState: unknown;
  /** State version for ordering - discard out-of-order updates */
  stateVersion: number;

  // WebRTC
  rtcState: ConnectionState | null;

  // Host status
  hostConnected: boolean;

  // Network status
  isOnline: boolean;

  // Message buffering during reconnection
  pendingMessages: PendingMessage[];
  hasPendingActions: boolean;

  // Room closed state (for showing friendly message)
  roomClosedReason: string | null;

  // Kicked state (for showing kicked message)
  wasKicked: boolean;

  // Reactions from other players
  lastReaction: ReactionInfo | null;

  // Actions
  join: (
    roomCode: string,
    playerName: string
  ) => Promise<{ success: boolean; error?: string; reason?: string }>;
  disconnect: () => void;
  leaveRoom: () => void;
  reconnect: () => void;
  sendInput: (action: string, data: unknown) => void;
  sendReaction: (reaction: string) => void;
  selectGame: (gameType: string) => void;
  startGame: () => void;
  returnToLobby: () => void;
  pauseGame: () => void;
  resumeGame: () => void;
  updateAvatar: (avatar: string, options?: { source?: AvatarUpdateSource }) => void;
  clearRoomClosedReason: () => void;
  clearKickedState: () => void;
}

// WebSocket URL configuration
// Supports explicit override via:
// 1. Build-time env variable: VITE_WS_URL
// 2. Window config: window.__MULTIPACK_WS_URL__
// Falls back to auto-detection based on current protocol
function getWebSocketUrl(): string {
  // Check for explicit configuration
  const envUrl = import.meta.env?.VITE_WS_URL;
  if (envUrl) {
    return envUrl;
  }

  // Check for window-level config (useful for proxied deployments)
  const windowUrl = window.__MULTIPACK_WS_URL__;
  if (windowUrl) {
    return windowUrl;
  }

  // Auto-detect based on current protocol
  // This works for most cases, including behind SSL-terminating proxies
  // where the browser correctly sees https://
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
}

const WS_URL = getWebSocketUrl();
const ICE_SERVERS = parseIceServers(import.meta.env?.VITE_ICE_SERVERS);

if (import.meta.env?.VITE_ICE_SERVERS && (!ICE_SERVERS || ICE_SERVERS.length === 0)) {
  logger.warn(
    { raw: import.meta.env.VITE_ICE_SERVERS },
    'Invalid VITE_ICE_SERVERS, using defaults'
  );
}

// Reconnection config
const RECONNECT_INITIAL_DELAY = 1000;
const RECONNECT_MAX_DELAY = 10000;
const RECONNECT_MAX_ATTEMPTS = 15;
// Auto-reload after max attempts (handles gateway rebuilds)
const AUTO_RELOAD_ON_MAX_ATTEMPTS = true;

let reconnectAttempts = 0;
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

// WebRTC reconnect config
const RTC_RECONNECT_INITIAL_DELAY = 1000;
const RTC_RECONNECT_MAX_DELAY = 10000;
const RTC_RECONNECT_MAX_ATTEMPTS = 5;

let rtcReconnectAttempts = 0;
let rtcReconnectTimeout: ReturnType<typeof setTimeout> | null = null;

function scheduleReconnect(reconnectFn: () => void): void {
  if (reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
    if (AUTO_RELOAD_ON_MAX_ATTEMPTS) {
      logger.warn('Max reconnection attempts reached, reloading page');
      window.location.reload();
      return;
    }
    logger.warn('Max reconnection attempts reached');
    usePlayerStore.setState({ connectionStatus: 'disconnected' });
    return;
  }

  const delay = Math.min(
    RECONNECT_INITIAL_DELAY * Math.pow(2, reconnectAttempts),
    RECONNECT_MAX_DELAY
  );

  logger.info({ attempt: reconnectAttempts + 1, delay }, 'Scheduling reconnect');

  reconnectTimeout = setTimeout(() => {
    reconnectAttempts++;
    reconnectFn();
  }, delay);
}

function clearRtcReconnect(): void {
  if (rtcReconnectTimeout) {
    clearTimeout(rtcReconnectTimeout);
    rtcReconnectTimeout = null;
  }
  rtcReconnectAttempts = 0;
}

function scheduleRtcReconnect(socket: ExtendedWebSocket): void {
  if (rtcReconnectTimeout) return;
  if (rtcReconnectAttempts >= RTC_RECONNECT_MAX_ATTEMPTS) {
    logger.warn({ attempts: rtcReconnectAttempts }, 'Max WebRTC reconnect attempts reached');
    return;
  }

  const delay = Math.min(
    RTC_RECONNECT_INITIAL_DELAY * Math.pow(2, rtcReconnectAttempts),
    RTC_RECONNECT_MAX_DELAY
  );

  rtcReconnectTimeout = setTimeout(() => {
    rtcReconnectTimeout = null;
    rtcReconnectAttempts++;

    if (socket.readyState === WebSocket.OPEN) {
      logger.info({ attempt: rtcReconnectAttempts }, 'Retrying WebRTC connection');
      requestRtcConnection(socket);
    }

    const { rtcState } = usePlayerStore.getState();
    if (rtcState && rtcState !== 'connected' && socket.readyState === WebSocket.OPEN) {
      scheduleRtcReconnect(socket);
    } else {
      clearRtcReconnect();
    }
  }, delay);
}

function cancelReconnect(): void {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
  reconnectAttempts = 0;
}

// Heartbeat configuration
const HEARTBEAT_INTERVAL = 30000; // Send PING every 30 seconds
const HEARTBEAT_TIMEOUT = 60000; // Consider connection stale after 60 seconds without PONG
const BACKGROUND_RECONNECT_THRESHOLD = 30000; // Proactively reconnect if backgrounded > 30 seconds (iOS Safari kills connections)

let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
let staleCheckInterval: ReturnType<typeof setInterval> | null = null;
let lastPongTime = Date.now();
let lastBackgroundTime: number | null = null;

/** Start the heartbeat to detect stale connections */
function startHeartbeat(socket: ExtendedWebSocket): void {
  stopHeartbeat();
  lastPongTime = Date.now();
  lastBackgroundTime = null;

  // Send PING every 30 seconds
  heartbeatInterval = setInterval(() => {
    if (socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'PING', sentAt: Date.now() }));
      logger.trace('Sent heartbeat PING');
    }
  }, HEARTBEAT_INTERVAL);

  // Check for stale connection every 10 seconds
  staleCheckInterval = setInterval(() => {
    const timeSinceLastPong = Date.now() - lastPongTime;
    if (timeSinceLastPong > HEARTBEAT_TIMEOUT) {
      logger.warn({ timeSinceLastPong }, 'Connection stale - no PONG received, reconnecting');
      socket.close();
      // The onclose handler will trigger reconnection
    }
  }, 10000);

  // Handle visibility changes (critical for iOS Safari which kills connections in background)
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      // Track when we went to background
      lastBackgroundTime = Date.now();
      logger.debug('App went to background');
    } else if (document.visibilityState === 'visible') {
      // App became visible - check if we were backgrounded too long
      const backgroundDuration = lastBackgroundTime ? Date.now() - lastBackgroundTime : 0;
      lastBackgroundTime = null;

      logger.debug({ backgroundDuration }, 'App became visible');

      // iOS Safari aggressively kills WebSocket connections after ~30 seconds in background
      // If we were backgrounded longer than that, proactively close and reconnect
      // rather than waiting for a failed PING which could take additional time
      if (backgroundDuration > BACKGROUND_RECONNECT_THRESHOLD) {
        logger.warn({ backgroundDuration }, 'App was backgrounded too long, forcing reconnection');
        // Clean up WebRTC connection - iOS Safari kills both WebSocket and WebRTC
        // when backgrounded. The old PeerConnection is stale and must be replaced.
        cleanupPeerConnection();
        // Close the socket - the onclose handler will trigger reconnection
        // After reconnection, requestRtcConnection will be called to re-establish WebRTC
        socket.close();
        return;
      }

      // Still within safe window - just send a PING to verify connection health
      if (socket.readyState === WebSocket.OPEN) {
        logger.debug('Sending post-resume heartbeat');
        socket.send(JSON.stringify({ type: 'PING', sentAt: Date.now() }));
      }
    }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Store the cleanup function
  socket.__heartbeatCleanup = () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}

/** Stop the heartbeat */
function stopHeartbeat(): void {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
  if (staleCheckInterval) {
    clearInterval(staleCheckInterval);
    staleCheckInterval = null;
  }
}

/** Update last PONG time (call when PONG is received) */
function updateLastPongTime(): void {
  lastPongTime = Date.now();
}

// PeerConnection instance for WebRTC to host (kept outside store)
let peerConnection: PeerConnection | null = null;

// Connection quality monitor (kept outside store for performance)
const connectionMonitor = new ConnectionMonitor(20);

// Export for components to subscribe
export function subscribeToConnectionMetrics(
  callback: (metrics: ConnectionMetrics) => void
): () => void {
  return connectionMonitor.subscribe(callback);
}

export function getConnectionMetrics(): ConnectionMetrics {
  return connectionMonitor.getMetrics();
}

/** Initialize or get the PeerConnection instance */
function initPeerConnection(
  socket: ExtendedWebSocket,
  options?: { reuseExisting?: boolean }
): PeerConnection {
  if (options?.reuseExisting && peerConnection && peerConnection.getState() !== 'closed') {
    return peerConnection;
  }

  if (peerConnection) {
    peerConnection.close();
  }

  peerConnection = new PeerConnection({
    peerId: 'host',
    isInitiator: false, // Controller receives offer from host
    logger: logger,
    iceServers: ICE_SERVERS,
    onStateChange: (state) => {
      logger.info({ state }, 'WebRTC connection state changed');
      usePlayerStore.setState({ rtcState: state });
      if (state === 'connected') {
        clearRtcReconnect();
        void peerConnection?.getSelectedCandidatePair().then((info) => {
          if (!info) return;
          logger.info({ local: info.local, remote: info.remote }, 'WebRTC selected candidate pair');
        });
        return;
      }
      if (state === 'failed' || state === 'disconnected') {
        const { connectionStatus } = usePlayerStore.getState();
        if (connectionStatus === 'connected') {
          scheduleRtcReconnect(socket);
        }
      }
    },
    onIceCandidate: (candidate) => {
      // Send ICE candidate to host via WebSocket signaling
      socket.send(
        JSON.stringify({
          type: 'RTC_ICE_CANDIDATE',
          targetId: 'host',
          candidate: candidate.candidate,
          sdpMid: candidate.sdpMid,
          sdpMLineIndex: candidate.sdpMLineIndex,
        })
      );
    },
    onMessage: (message) => {
      logger.debug({ type: message.type }, 'Received message via WebRTC');

      // Handle state updates received directly from host via WebRTC
      if (message.type === 'STATE_UPDATE') {
        const payload = message.payload as { version: number; state: unknown; phase?: string };
        const currentVersion = usePlayerStore.getState().stateVersion;

        // Apply same version check as WebSocket path (discard out-of-order)
        if (payload.version <= currentVersion) {
          logger.trace(
            { incoming: payload.version, current: currentVersion },
            'Discarding out-of-order WebRTC state update'
          );
          return;
        }

        // Log round-trip latency if we have a send timestamp
        const lastSendTime = (globalThis as Record<string, unknown>).__lastActionSendTime as
          | number
          | undefined;
        if (lastSendTime) {
          const latencyMs = Math.round(performance.now() - lastSendTime);
          logger.info({ latencyMs, via: 'webrtc' }, 'State update latency');
          (globalThis as Record<string, unknown>).__lastActionSendTime = undefined;
        }

        usePlayerStore.setState({
          privateState: payload.state,
          stateVersion: payload.version,
        });
        logger.trace({ version: payload.version, via: 'webrtc' }, 'State updated via WebRTC');
      }
    },
    onBufferOverflow: (message) => {
      // WebRTC buffer overflow - fall back to WebSocket
      logger.warn({ type: message.type }, 'WebRTC buffer overflow, using WebSocket fallback');
      const currentSocket = usePlayerStore.getState().socket;
      if (currentSocket?.readyState === WebSocket.OPEN && message.type === 'PLAYER_INPUT') {
        const payload = message.payload as { action: string; data: unknown; sequence: number };
        currentSocket.send(
          JSON.stringify({
            type: 'PLAYER_INPUT',
            action: payload.action,
            data: payload.data,
            sequence: payload.sequence,
          })
        );
      }
    },
  });

  return peerConnection;
}

/** Clean up the PeerConnection */
function cleanupPeerConnection(): void {
  if (peerConnection) {
    peerConnection.close();
    peerConnection = null;
  }
  clearRtcReconnect();
}

/** Flush pending messages after reconnection */
function flushPendingMessages(socket: ExtendedWebSocket): void {
  const { pendingMessages } = usePlayerStore.getState();
  const now = Date.now();
  let sentCount = 0;
  let expiredCount = 0;

  for (const item of pendingMessages) {
    // Only send messages that aren't too old
    if (now - item.timestamp < PENDING_MESSAGE_TTL) {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify(item.message));
        sentCount++;
        logger.debug(
          { action: item.message.action, age: now - item.timestamp },
          'Sent buffered message'
        );
      }
    } else {
      expiredCount++;
      logger.info(
        { action: item.message.action, age: now - item.timestamp },
        'Discarded expired buffered message'
      );
    }
  }

  // Clear all pending messages
  usePlayerStore.setState({ pendingMessages: [], hasPendingActions: false });

  if (sentCount > 0 || expiredCount > 0) {
    logger.info({ sentCount, expiredCount }, 'Flushed pending messages');
  }
}

/** Request WebRTC connection to host */
function requestRtcConnection(socket: ExtendedWebSocket): void {
  logger.info('Requesting WebRTC connection to host');
  socket.send(JSON.stringify({ type: 'RTC_CONNECT' }));
}

function pickUniqueLobbyAvatar(
  preferred: string,
  players: PlayerInfo[],
  spectators: PlayerInfo[],
  playerId: string
): string {
  const usedAvatars = new Set<string>();
  for (const player of players) {
    if (player.id !== playerId && player.avatar) {
      usedAvatars.add(player.avatar);
    }
  }
  for (const spectator of spectators) {
    if (spectator.id !== playerId && spectator.avatar) {
      usedAvatars.add(spectator.avatar);
    }
  }

  if (!usedAvatars.has(preferred)) {
    return preferred;
  }

  const available = AVATAR_TYPES.filter((avatar) => !usedAvatars.has(avatar));
  if (available.length === 0) {
    return preferred;
  }

  return available[Math.floor(Math.random() * available.length)] ?? preferred;
}

function attemptReconnect(
  get: () => PlayerState,
  set: (state: Partial<PlayerState>) => void
): void {
  const { sessionToken, roomCode, playerName, playerAvatar, isAvatarChosen } = get();

  // Need either a session token or room code + player name to reconnect
  if (!sessionToken && !(roomCode && playerName)) {
    set({ connectionStatus: 'disconnected' });
    return;
  }

  logger.info({ hasSession: !!sessionToken, roomCode }, 'Attempting reconnect...');

  const shouldEnsureUnique = !isAvatarChosen || !playerAvatar;
  const fallbackAvatar = playerAvatar ?? getRandomAvatar();
  const socket = new WebSocket(WS_URL);

  socket.onopen = () => {
    logger.info('Connected, sending reconnect request');
    reconnectAttempts = 0;
    set({ socket });

    // Set up log forwarding to server
    setLogSocket(socket);

    if (sessionToken) {
      // Try session-based reconnection first
      socket.send(
        JSON.stringify({
          type: 'RECONNECT',
          sessionToken,
        })
      );
    } else if (roomCode && playerName) {
      // No session, try to rejoin directly
      socket.send(
        JSON.stringify({
          type: 'JOIN_ROOM',
          roomCode: roomCode.toUpperCase(),
          playerName,
          avatar: fallbackAvatar,
        })
      );
    }
  };

  socket.onmessage = (event) => {
    try {
      const message = JSON.parse(event.data);

      if (message.type === 'RECONNECT_RESULT') {
        if (message.success) {
          set({
            connectionStatus: 'connected',
            playerId: message.playerId as string,
            roomCode: message.roomInfo.roomCode,
            roomId: message.roomInfo.roomId,
            hostName: message.roomInfo.hostName,
            phase: message.roomInfo.phase,
            players: message.roomInfo.players ?? [],
            spectators: message.roomInfo.spectators ?? [],
            isOwner: (message.isOwner as boolean) ?? false,
            isSpectator: (message.isSpectator as boolean) ?? false,
            owner: (message.roomInfo.owner as OwnerInfo) ?? null,
            gameType: message.roomInfo.gameType ?? null,
            // Include game state if provided (for in-progress games)
            // This prevents race condition where UI renders before STATE_UPDATE arrives
            privateState: message.gameState ?? null,
            stateVersion: message.gameStateVersion ?? get().stateVersion,
          });
          // Start heartbeat for reconnected session
          startHeartbeat(socket);
          // Flush any messages that were buffered during reconnection
          flushPendingMessages(socket);
          // Re-establish WebRTC connection (iOS Safari kills it when backgrounded)
          requestRtcConnection(socket);
          logger.info(
            { isOwner: message.isOwner, hasGameState: !!message.gameState },
            'Reconnected successfully'
          );
        } else {
          // Session invalid - try to rejoin with room code and player name
          const { roomCode, playerName } = get();
          if (roomCode && playerName) {
            logger.info({ roomCode }, 'Session invalid, attempting to rejoin room');
            socket.send(
              JSON.stringify({
                type: 'JOIN_ROOM',
                roomCode: roomCode.toUpperCase(),
                playerName,
                avatar: fallbackAvatar,
              })
            );
          } else {
            logger.warn('Reconnect failed and no room code to rejoin, clearing session');
            set({
              connectionStatus: 'disconnected',
              sessionToken: null,
              playerId: null,
            });
            socket.close();
          }
        }
        return;
      }

      // Handle JOIN_RESULT during reconnection (fallback rejoin)
      if (message.type === 'JOIN_RESULT') {
        if (message.success) {
          const roomPlayers = (message.roomInfo.players as PlayerInfo[]) ?? [];
          const roomSpectators = (message.roomInfo.spectators as PlayerInfo[]) ?? [];
          set({
            connectionStatus: 'connected',
            playerId: message.playerId as string,
            sessionToken: message.sessionToken as string,
            roomCode: message.roomInfo.roomCode as string,
            roomId: message.roomInfo.roomId as string,
            hostName: message.roomInfo.hostName as string,
            phase: message.roomInfo.phase as GamePhase,
            players: roomPlayers,
            spectators: roomSpectators,
            isOwner: (message.isOwner as boolean) ?? false,
            isSpectator: (message.isSpectator as boolean) ?? false,
            owner: (message.roomInfo.owner as OwnerInfo) ?? null,
            gameType: (message.roomInfo.gameType as string) ?? null,
            // Include game state if provided (for in-progress games)
            // This prevents race condition where UI renders before STATE_UPDATE arrives
            privateState: message.gameState ?? null,
          });
          if (shouldEnsureUnique && message.roomInfo.phase === 'lobby') {
            const uniqueAvatar = pickUniqueLobbyAvatar(
              fallbackAvatar,
              roomPlayers,
              roomSpectators,
              message.playerId as string
            );
            if (uniqueAvatar !== fallbackAvatar) {
              get().updateAvatar(uniqueAvatar, { source: 'auto' });
            }
          }
          // Start heartbeat for rejoined session
          startHeartbeat(socket);
          // Flush any messages that were buffered during reconnection
          flushPendingMessages(socket);
          // Re-establish WebRTC connection (iOS Safari kills it when backgrounded)
          requestRtcConnection(socket);
          logger.info(
            { roomCode: message.roomInfo.roomCode, hasGameState: !!message.gameState },
            'Rejoined room successfully'
          );
        } else {
          logger.warn({ error: message.error }, 'Failed to rejoin room, clearing session');
          set({
            connectionStatus: 'disconnected',
            sessionToken: null,
            playerId: null,
            roomCode: null,
          });
          socket.close();
        }
        return;
      }

      handleMessage(message, set, get);
    } catch (err) {
      logger.error({ err }, 'Failed to parse message');
    }
  };

  socket.onclose = () => {
    const { sessionToken: token, roomCode, playerName } = get();
    // Attempt reconnection if we have a session token OR if we have room code + player name
    if (token || (roomCode && playerName)) {
      set({ connectionStatus: 'reconnecting', socket: null });
      scheduleReconnect(() => attemptReconnect(get, set));
    } else {
      set({ connectionStatus: 'disconnected', socket: null });
    }
  };

  socket.onerror = (err) => {
    logger.error({ err }, 'WebSocket error during reconnect');
  };
}

// Persist configuration for non-test-harness mode
const persistConfig: PersistOptions<
  PlayerState,
  Pick<
    PlayerState,
    'sessionToken' | 'playerName' | 'playerAvatar' | 'isAvatarChosen' | 'playerId' | 'roomCode'
  >
> = {
  name: 'multipack-player',
  partialize: (state) => ({
    sessionToken: state.sessionToken,
    playerName: state.playerName,
    playerAvatar: state.playerAvatar,
    isAvatarChosen: state.isAvatarChosen,
    playerId: state.playerId,
    roomCode: state.roomCode,
  }),
};

// Store creator function (shared between persisted and non-persisted versions)
const createPlayerStoreSlice: StateCreator<PlayerState> = (set, get) => ({
  connectionStatus: 'disconnected',
  socket: null,
  sessionToken: null,
  playerId: null,
  playerName: null,
  playerAvatar: null,
  isAvatarChosen: false,
  roomCode: null,
  roomId: null,
  hostName: null,
  phase: 'lobby',
  players: [],
  spectators: [],
  isOwner: false,
  isSpectator: false,
  owner: null,
  gameType: null,
  gameInfo: null,
  gameCatalog: [],
  privateState: null,
  stateVersion: 0,
  rtcState: null,
  hostConnected: true,
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  pendingMessages: [],
  hasPendingActions: false,
  roomClosedReason: null,
  wasKicked: false,
  lastReaction: null,

  join: async (
    roomCode: string,
    playerName: string
  ): Promise<{ success: boolean; error?: string; reason?: string }> => {
    const { socket: existingSocket, playerAvatar: existingAvatar, isAvatarChosen } = get();

    if (existingSocket) {
      existingSocket.close();
    }

    // Use existing avatar or generate a new one
    const avatar = existingAvatar ?? getRandomAvatar();
    const shouldEnsureUnique = !isAvatarChosen || !existingAvatar;
    set({ playerAvatar: avatar });

    // Single join attempt function
    const attemptJoin = (): Promise<{ success: boolean; error?: string; reason?: string }> => {
      return new Promise((resolve) => {
        set({ connectionStatus: 'connecting' });
        logger.info({ roomCode, avatar, wsUrl: WS_URL }, 'Joining room...');

        const socket = new WebSocket(WS_URL);
        let resolved = false;

        socket.onopen = () => {
          logger.info('Connected to gateway');
          reconnectAttempts = 0; // Reset on successful connection
          set({ connectionStatus: 'connected', socket });

          // Set up log forwarding to server
          setLogSocket(socket);

          // Send join request
          socket.send(
            JSON.stringify({
              type: 'JOIN_ROOM',
              roomCode: roomCode.toUpperCase(),
              playerName,
              avatar,
            })
          );
        };

        socket.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);

            // Handle join result first
            if (message.type === 'JOIN_RESULT' && !resolved) {
              resolved = true;
              if (message.success) {
                const roomPlayers = (message.roomInfo.players as PlayerInfo[]) ?? [];
                const roomSpectators = (message.roomInfo.spectators as PlayerInfo[]) ?? [];
                set({
                  playerId: message.playerId as string,
                  playerName,
                  sessionToken: message.sessionToken as string,
                  roomCode: message.roomInfo.roomCode as string,
                  roomId: message.roomInfo.roomId as string,
                  hostName: message.roomInfo.hostName as string,
                  phase: message.roomInfo.phase as GamePhase,
                  players: roomPlayers,
                  spectators: roomSpectators,
                  isOwner: (message.isOwner as boolean) ?? false,
                  isSpectator: (message.isSpectator as boolean) ?? false,
                  owner: (message.roomInfo.owner as OwnerInfo) ?? null,
                  gameType: (message.roomInfo.gameType as string) ?? null,
                  // Include game state if provided (for in-progress games)
                  // This prevents race condition where UI renders before STATE_UPDATE arrives
                  privateState: message.gameState ?? null,
                });
                if (shouldEnsureUnique && message.roomInfo.phase === 'lobby') {
                  const uniqueAvatar = pickUniqueLobbyAvatar(
                    avatar,
                    roomPlayers,
                    roomSpectators,
                    message.playerId as string
                  );
                  if (uniqueAvatar !== avatar) {
                    get().updateAvatar(uniqueAvatar, { source: 'auto' });
                  }
                }
                logger.info(
                  {
                    roomCode: message.roomInfo.roomCode,
                    isOwner: message.isOwner,
                    isSpectator: message.isSpectator,
                    hasGameState: !!message.gameState,
                  },
                  'Joined room'
                );
                // Start heartbeat to detect stale connections
                startHeartbeat(socket);
                // Request WebRTC connection to host for low-latency communication
                requestRtcConnection(socket);
                resolve({ success: true });
              } else {
                logger.warn(
                  { error: message.error, reason: message.reason },
                  'Failed to join room'
                );
                set({ connectionStatus: 'disconnected' });
                socket.close();
                resolve({
                  success: false,
                  error: message.error as string,
                  reason: message.reason as string | undefined,
                });
              }
              return;
            }

            handleMessage(message, set, get);
          } catch (err) {
            logger.error({ err }, 'Failed to parse message');
          }
        };

        socket.onclose = () => {
          logger.info('Disconnected from gateway');
          if (!resolved) {
            resolved = true;
            resolve({ success: false, error: 'CONNECTION_FAILED' });
          }

          const { sessionToken } = get();
          // If we have a session, try to reconnect
          if (sessionToken) {
            set({ connectionStatus: 'reconnecting', socket: null });
            scheduleReconnect(() => attemptReconnect(get, set));
          } else {
            set({ connectionStatus: 'disconnected', socket: null });
          }
        };

        socket.onerror = (err) => {
          logger.error({ err, readyState: socket.readyState, url: WS_URL }, 'WebSocket error');
        };

        // Timeout (15 seconds for slow networks)
        setTimeout(() => {
          if (!resolved) {
            resolved = true;
            logger.warn({ readyState: socket.readyState, url: WS_URL }, 'Join timed out');
            socket.close();
            set({ connectionStatus: 'disconnected' });
            resolve({ success: false, error: 'TIMEOUT' });
          }
        }, 15000);
      });
    };

    // Retry loop with exponential backoff
    let lastError: string | undefined;
    for (let attempt = 0; attempt <= JOIN_MAX_RETRIES; attempt++) {
      if (attempt > 0) {
        const delay = JOIN_RETRY_BASE_DELAY * Math.pow(2, attempt - 1);
        logger.info({ attempt, delay, lastError }, 'Retrying join after delay');
        await new Promise((r) => setTimeout(r, delay));
      }

      const result = await attemptJoin();

      if (result.success) {
        return result;
      }

      lastError = result.error;

      // Don't retry for definitive errors
      if (result.error && NON_RETRYABLE_ERRORS.includes(result.error)) {
        logger.info({ error: result.error }, 'Not retrying - definitive error');
        return result;
      }

      // Continue to next attempt for retryable errors (TIMEOUT, CONNECTION_FAILED)
      logger.warn(
        { attempt, maxRetries: JOIN_MAX_RETRIES, error: result.error },
        'Join attempt failed, will retry'
      );
    }

    // All retries exhausted
    logger.error({ attempts: JOIN_MAX_RETRIES + 1, lastError }, 'All join attempts failed');
    return { success: false, error: lastError ?? 'CONNECTION_FAILED' };
  },

  disconnect: () => {
    cancelReconnect();
    stopHeartbeat();
    cleanupPeerConnection();
    const { socket } = get();
    if (socket) {
      socket.__heartbeatCleanup?.();
      socket.close();
    }
    // Keep playerName and playerAvatar so they persist for future sessions
    set({
      connectionStatus: 'disconnected',
      socket: null,
      sessionToken: null,
      playerId: null,
      roomCode: null,
      roomId: null,
      phase: 'lobby',
      players: [],
      spectators: [],
      isOwner: false,
      isSpectator: false,
      owner: null,
      gameType: null,
      gameInfo: null,
      gameCatalog: [],
      stateVersion: 0,
      rtcState: null,
    });
  },

  leaveRoom: () => {
    const { socket } = get();
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'LEAVE_ROOM' }));
    }
    cancelReconnect();
    stopHeartbeat();
    cleanupPeerConnection();
    if (socket) {
      socket.__heartbeatCleanup?.();
      socket.close();
    }
    // Keep playerName and playerAvatar so they can easily rejoin
    set({
      connectionStatus: 'disconnected',
      socket: null,
      sessionToken: null,
      playerId: null,
      roomCode: null,
      roomId: null,
      hostName: null,
      phase: 'lobby',
      players: [],
      spectators: [],
      isOwner: false,
      isSpectator: false,
      owner: null,
      gameType: null,
      gameInfo: null,
      gameCatalog: [],
      privateState: null,
      stateVersion: 0,
      rtcState: null,
    });
    logger.info('Left room');
  },

  sendInput: (action: string, data: unknown) => {
    const { socket, pendingMessages } = get();
    const sequence = Date.now();

    // Track action send time for latency measurement
    const sendTimestamp = performance.now();
    (globalThis as Record<string, unknown>).__lastActionSendTime = sendTimestamp;

    // Try WebRTC first if connected (lower latency, direct to host)
    if (peerConnection?.isConnected()) {
      const rtcMessage: DataChannelMessage = {
        type: 'PLAYER_INPUT',
        payload: { action, data, sequence },
      };
      const result = peerConnection.send(rtcMessage);

      if (result === 'sent' || result === 'queued') {
        logger.trace({ action, result, via: 'webrtc' }, 'Sent input via WebRTC');
        return;
      }
      // WebRTC failed, fall through to WebSocket
      logger.debug({ action, result }, 'WebRTC send failed, falling back to WebSocket');
    }

    // WebSocket fallback
    const message = {
      type: 'PLAYER_INPUT' as const,
      action,
      data,
      sequence,
    };

    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message));
      logger.trace({ action, via: 'websocket' }, 'Sent input via WebSocket');
      return;
    }

    // Buffer message for later delivery
    logger.info({ action }, 'Buffering message during reconnection');
    set({
      pendingMessages: [...pendingMessages, { message, timestamp: Date.now() }],
      hasPendingActions: true,
    });
  },

  sendReaction: (reaction: string) => {
    const { socket } = get();
    if (!socket || socket.readyState !== WebSocket.OPEN) return;

    socket.send(
      JSON.stringify({
        type: 'PLAYER_REACTION',
        reaction,
      })
    );
  },

  selectGame: (gameType: string) => {
    const { socket, isOwner } = get();
    if (!socket || !isOwner) return;

    socket.send(
      JSON.stringify({
        type: 'SELECT_GAME',
        gameType,
      })
    );
  },

  startGame: () => {
    const { socket, isOwner, gameType } = get();
    if (!socket || !isOwner || !gameType) return;

    socket.send(
      JSON.stringify({
        type: 'START_GAME',
        gameType,
      })
    );
  },

  returnToLobby: () => {
    const { socket, isOwner } = get();
    if (!socket || !isOwner) return;

    socket.send(
      JSON.stringify({
        type: 'RETURN_TO_LOBBY',
      })
    );
  },

  pauseGame: () => {
    const { socket, isOwner, phase } = get();
    if (!socket || !isOwner || phase !== 'playing') return;

    socket.send(
      JSON.stringify({
        type: 'PAUSE_GAME',
      })
    );
  },

  resumeGame: () => {
    const { socket, isOwner, phase } = get();
    if (!socket || !isOwner || phase !== 'paused') return;

    socket.send(
      JSON.stringify({
        type: 'RESUME_GAME',
      })
    );
  },

  updateAvatar: (avatar: string, options?: { source?: AvatarUpdateSource }) => {
    const { socket } = get();
    if (!socket || socket.readyState !== WebSocket.OPEN) return;

    // Update local state immediately for responsive feel
    const source = options?.source ?? 'manual';
    set((state) => ({
      playerAvatar: avatar,
      isAvatarChosen: source === 'manual' ? true : state.isAvatarChosen,
    }));

    // Send update to server
    socket.send(
      JSON.stringify({
        type: 'UPDATE_AVATAR',
        avatar,
      })
    );
  },

  clearRoomClosedReason: () => {
    set({ roomClosedReason: null });
  },

  clearKickedState: () => {
    set({ wasKicked: false });
  },

  reconnect: () => {
    const { sessionToken, connectionStatus } = get();
    if (!sessionToken || connectionStatus === 'connecting' || connectionStatus === 'reconnecting') {
      return;
    }
    set({ connectionStatus: 'reconnecting' });
    attemptReconnect(get, set);
  },
});

// Create the store - with or without persistence based on test harness mode
// In test harness mode (iframes with autoJoin), skip localStorage to avoid conflicts
export const usePlayerStore = isTestHarnessMode
  ? create<PlayerState>()(createPlayerStoreSlice)
  : create<PlayerState>()(persist(createPlayerStoreSlice, persistConfig));

// Expose the store on window for e2e tests to access connection state
if (typeof window !== 'undefined') {
  window.__ZUSTAND_PLAYER_STORE__ = {
    getState: usePlayerStore.getState,
    setState: usePlayerStore.setState,
    subscribe: usePlayerStore.subscribe,
  };
}

// Network status detection - handles browser online/offline events
let networkListenersInitialized = false;

function initNetworkStatusListeners(): void {
  if (networkListenersInitialized || typeof window === 'undefined') return;
  networkListenersInitialized = true;

  // Handle going offline
  window.addEventListener('offline', () => {
    logger.warn('Network offline detected');
    const { connectionStatus } = usePlayerStore.getState();

    // Update online status
    usePlayerStore.setState({ isOnline: false });

    // If we're connected, the socket will likely close on its own,
    // but we can proactively update the UI
    if (connectionStatus === 'connected' || connectionStatus === 'connecting') {
      usePlayerStore.setState({ connectionStatus: 'reconnecting' });
    }

    // Vibrate to alert user
    if ('vibrate' in navigator) {
      navigator.vibrate([200, 100, 200]);
    }
  });

  // Handle coming back online
  window.addEventListener('online', () => {
    logger.info('Network online detected');

    // Update online status
    usePlayerStore.setState({ isOnline: true });

    const { connectionStatus, sessionToken, roomCode, playerName } = usePlayerStore.getState();

    // If we're disconnected or reconnecting and have credentials, attempt to reconnect
    if (
      (connectionStatus === 'disconnected' || connectionStatus === 'reconnecting') &&
      (sessionToken || (roomCode && playerName))
    ) {
      logger.info('Network restored, triggering reconnection');
      // Cancel any pending reconnect to avoid double attempts
      cancelReconnect();
      reconnectAttempts = 0; // Reset attempts on fresh network
      usePlayerStore.setState({ connectionStatus: 'reconnecting' });
      attemptReconnect(usePlayerStore.getState, usePlayerStore.setState);
    }
  });

  logger.debug('Network status listeners initialized');
}

// Initialize network listeners when module loads
initNetworkStatusListeners();

function handleMessage(
  message: { type: string; [key: string]: unknown },
  set: (state: Partial<PlayerState>) => void,
  get: () => PlayerState
) {
  logger.debug({ type: message.type }, 'Received message');

  switch (message.type) {
    case 'PLAYER_JOINED': {
      const playerId = message.playerId as string;
      const existingPlayers = get().players;

      // Avoid duplicates - can happen during reconnection or message replay
      if (existingPlayers.some((p) => p.id === playerId)) {
        logger.debug({ playerId }, 'Ignoring duplicate PLAYER_JOINED');
        break;
      }

      const player: PlayerInfo = {
        id: playerId,
        name: message.playerName as string,
        avatar: message.avatar as string | undefined,
        connectionStatus: 'connected',
        score: 0,
      };
      set({ players: [...existingPlayers, player] });
      break;
    }

    case 'PLAYER_LEFT': {
      const players = get().players.filter((p) => p.id !== message.playerId);
      const spectators = get().spectators.filter((p) => p.id !== message.playerId);
      set({ players, spectators });
      break;
    }

    case 'OBSERVER_LEFT': {
      // Remove observer from spectators list (observers are shown as spectators on controller)
      const spectators = get().spectators.filter((p) => p.id !== message.playerId);
      set({ spectators });
      break;
    }

    case 'OBSERVER_JOINED': {
      // Add observer to spectators list (observers join during active games)
      const observer: PlayerInfo = {
        id: message.playerId as string,
        name: message.playerName as string,
        avatar: message.avatar as string | undefined,
        connectionStatus: 'connected' as const,
        score: 0,
      };
      // Avoid duplicates
      const existingIds = new Set(get().spectators.map((s) => s.id));
      if (!existingIds.has(observer.id)) {
        set({ spectators: [...get().spectators, observer] });
      }
      break;
    }

    case 'PLAYER_RECONNECTED': {
      const reconnectedPlayerId = message.playerId as string;
      const { players, spectators } = get();
      // Update connectionStatus to 'connected' in both lists
      const updatedPlayers = players.map((p) =>
        p.id === reconnectedPlayerId ? { ...p, connectionStatus: 'connected' as const } : p
      );
      const updatedSpectators = spectators.map((s) =>
        s.id === reconnectedPlayerId ? { ...s, connectionStatus: 'connected' as const } : s
      );
      set({ players: updatedPlayers, spectators: updatedSpectators });
      logger.info({ playerId: reconnectedPlayerId }, 'Player reconnected');
      break;
    }

    case 'PLAYER_DISCONNECTED': {
      const disconnectedPlayerId = message.playerId as string;
      const { players, spectators } = get();
      const updatedPlayers = players.map((p) =>
        p.id === disconnectedPlayerId ? { ...p, connectionStatus: 'disconnected' as const } : p
      );
      const updatedSpectators = spectators.map((s) =>
        s.id === disconnectedPlayerId ? { ...s, connectionStatus: 'disconnected' as const } : s
      );
      set({ players: updatedPlayers, spectators: updatedSpectators });
      logger.info({ playerId: disconnectedPlayerId }, 'Player disconnected');
      break;
    }

    case 'OWNER_CHANGED': {
      const newOwnerId = message.newOwnerId as string;
      const newOwnerName = message.newOwnerName as string;
      const { playerId } = get();
      set({
        owner: { id: newOwnerId, name: newOwnerName },
        isOwner: playerId === newOwnerId,
      });
      logger.info({ newOwnerId, newOwnerName }, 'Owner changed');
      break;
    }

    case 'AVATAR_CHANGED': {
      const changedPlayerId = message.playerId as string;
      const newAvatar = message.avatar as string;
      const { players, spectators, playerId } = get();

      // Update in players list
      const updatedPlayers = players.map((p) =>
        p.id === changedPlayerId ? { ...p, avatar: newAvatar } : p
      );

      // Update in spectators list
      const updatedSpectators = spectators.map((s) =>
        s.id === changedPlayerId ? { ...s, avatar: newAvatar } : s
      );

      set({
        players: updatedPlayers,
        spectators: updatedSpectators,
        // Also update own avatar if it's us
        ...(changedPlayerId === playerId ? { playerAvatar: newAvatar } : {}),
      });
      break;
    }

    case 'PROMOTED_TO_PLAYER': {
      // Spectator was promoted to player
      set({ isSpectator: false });
      logger.info({ message: message.message }, 'Promoted from spectator to player');
      // Vibrate to notify the user
      if ('vibrate' in navigator) {
        navigator.vibrate([100, 50, 100, 50, 100]); // Triple vibrate to get attention
      }
      break;
    }

    case 'DEMOTED_TO_SPECTATOR': {
      // Player was demoted to spectator (e.g., selected game has fewer max players)
      set({ isSpectator: true });
      logger.info(
        { message: message.message, reason: message.reason },
        'Demoted from player to spectator'
      );
      // Vibrate to alert the user
      if ('vibrate' in navigator) {
        navigator.vibrate([200, 100, 200]); // Two longer vibrations
      }
      break;
    }

    case 'GAME_SELECTED':
      set({
        gameType: message.gameType as string,
        gameInfo: {
          gameType: message.gameType as string,
          gameName: message.gameName as string,
          minPlayers: message.minPlayers as number,
          maxPlayers: message.maxPlayers as number,
          displayColor: message.displayColor as string | undefined,
          icon: message.icon as string | undefined,
        },
      });
      // Vibrate on game selected
      if ('vibrate' in navigator) {
        navigator.vibrate(100);
      }
      break;

    case 'GAME_CATALOG':
      set({ gameCatalog: (message.games as GameCatalogEntry[]) ?? [] });
      break;

    case 'GAME_STARTED':
      set({
        phase: 'playing',
        gameType: message.gameType as string,
      });
      // Vibrate on game start
      if ('vibrate' in navigator) {
        navigator.vibrate(200);
      }
      break;

    case 'STATE_UPDATE': {
      // Validate version to discard out-of-order messages (issue #308)
      // This prevents state inconsistencies when messages arrive via different transports
      const incomingVersion = message.version as number | undefined;
      const currentVersion = get().stateVersion;
      const previousVersion = message.previousVersion as number | undefined;

      // If no version provided (legacy/resync), accept it
      // If version is older than what we have, discard (out-of-order)
      const allowDuplicate = previousVersion !== undefined && incomingVersion === currentVersion;
      if (
        incomingVersion !== undefined &&
        (incomingVersion < currentVersion ||
          (incomingVersion === currentVersion && !allowDuplicate))
      ) {
        logger.debug({ incomingVersion, currentVersion }, 'Discarding out-of-order state update');
        break;
      }

      // Log round-trip latency if we have a send timestamp
      const lastSendTime = (globalThis as Record<string, unknown>).__lastActionSendTime as
        | number
        | undefined;
      if (lastSendTime) {
        const latencyMs = Math.round(performance.now() - lastSendTime);
        logger.info({ latencyMs, via: 'websocket' }, 'State update latency');
        (globalThis as Record<string, unknown>).__lastActionSendTime = undefined;
      }

      // This contains the player's private state view
      // Don't overwrite room phase with game's internal phase
      set({
        privateState: message.state,
        stateVersion: incomingVersion ?? currentVersion,
      });
      break;
    }

    case 'GAME_ENDED':
      set({ phase: 'results' });
      break;

    case 'RETURNED_TO_LOBBY':
      set({
        phase: 'lobby',
        gameType: null,
        gameInfo: null,
        privateState: null,
        stateVersion: 0,
        players: get().players.map((p) => ({ ...p, score: 0 })),
      });
      // Vibrate on return to lobby
      if ('vibrate' in navigator) {
        navigator.vibrate(100);
      }
      logger.info('Returned to lobby');
      break;

    case 'GAME_PAUSED':
      set({ phase: 'paused' });
      // Light vibration to indicate pause
      if ('vibrate' in navigator) {
        navigator.vibrate([50, 50, 50]);
      }
      logger.info({ pausedAt: message.pausedAt, pausedBy: message.pausedBy }, 'Game paused');
      break;

    case 'GAME_RESUMED':
      set({ phase: 'playing' });
      // Single vibration to indicate resume
      if ('vibrate' in navigator) {
        navigator.vibrate(100);
      }
      logger.info({ resumedAt: message.resumedAt }, 'Game resumed');
      break;

    case 'KICKED':
      cleanupPeerConnection();
      set({
        playerId: null,
        roomCode: null,
        sessionToken: null,
        phase: 'lobby',
        rtcState: null,
        wasKicked: true,
      });
      // Clear any stored session since we were kicked
      sessionStorage.removeItem('multipack_session');
      break;

    case 'ROOM_CLOSED': {
      const reason = (message.reason as string) ?? 'The room was closed';
      logger.info({ reason }, 'Room closed');
      cancelReconnect();
      cleanupPeerConnection();
      get().socket?.close();
      set({
        connectionStatus: 'disconnected',
        socket: null,
        sessionToken: null,
        playerId: null,
        roomCode: null,
        phase: 'lobby',
        isOwner: false,
        owner: null,
        gameType: null,
        gameInfo: null,
        gameCatalog: [],
        rtcState: null,
        roomClosedReason: reason,
      });
      break;
    }

    case 'SESSION_REPLACED':
      // Another tab/device connected with the same session
      logger.warn({ message: message.message }, 'Session replaced by another connection');
      cancelReconnect();
      cleanupPeerConnection();
      // Don't close socket - it was already closed by server
      // Clear session token to prevent onclose from scheduling a reconnect
      set({
        connectionStatus: 'disconnected',
        socket: null,
        sessionToken: null, // Clear to prevent auto-reconnect loop
        playerId: null,
        phase: 'lobby',
        rtcState: null,
      });
      // Show alert to user (but not in test harness mode where this is expected)
      if (!isTestHarnessMode) {
        alert('You were disconnected because you connected from another device or tab.');
      }
      break;

    case 'ERROR':
      logger.error({ code: message.code, message: message.message }, 'Server error');
      break;

    case 'PONG': {
      // Update last PONG time for heartbeat tracking
      updateLastPongTime();
      const latency = Date.now() - (message.sentAt as number);
      // Record latency for connection quality monitoring
      connectionMonitor.recordLatency(latency);
      logger.trace({ latency }, 'Received PONG');
      break;
    }

    case 'LATENCY_PING': {
      // Respond to latency ping from gateway
      const { socket } = get();
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'LATENCY_PONG' }));
      }
      break;
    }

    case 'HOST_STATUS': {
      const connected = message.connected as boolean;
      set({ hostConnected: connected });
      logger.info({ connected, message: message.message }, 'Host status changed');
      // Vibrate to alert user of host disconnect
      if (!connected && 'vibrate' in navigator) {
        navigator.vibrate([100, 100, 100]);
      }
      break;
    }

    case 'ACTION_ACK': {
      logger.info(
        {
          playerId: message.playerId as string,
          sequence: message.sequence as number,
          accepted: message.accepted as boolean,
        },
        'Received ACTION_ACK'
      );
      break;
    }

    // WebRTC signaling messages
    case 'RTC_OFFER_RELAY': {
      // Host sent us an offer, create answer
      const sdp = message.sdp as string;
      const restart = message.restart === true;
      const { socket } = get();
      if (!socket) break;

      const pc = initPeerConnection(socket, { reuseExisting: restart });
      pc.createAnswer(sdp)
        .then((answer) => {
          // Send answer back to host via WebSocket signaling
          socket.send(
            JSON.stringify({
              type: 'RTC_ANSWER',
              targetId: 'host',
              sdp: answer,
            })
          );
          logger.info('Created WebRTC answer for host');
        })
        .catch((err) => {
          logger.error({ err }, 'Failed to create WebRTC answer');
        });
      break;
    }

    case 'RTC_ICE_CANDIDATE_RELAY': {
      // Host sent us an ICE candidate
      const candidate = message.candidate as string;
      const sdpMid = message.sdpMid as string | null;
      const sdpMLineIndex = message.sdpMLineIndex as number | null;

      if (peerConnection) {
        peerConnection.addIceCandidate(candidate, sdpMid, sdpMLineIndex).catch((err) => {
          logger.error({ err }, 'Failed to add ICE candidate');
        });
      }
      break;
    }

    case 'EFFECT': {
      // Dispatch effect to EffectProvider
      const effect: Effect = {
        type: 'EFFECT',
        effect: message.effect as Effect['effect'],
        target: message.target as Effect['target'],
        data: message.data as Effect['data'],
        timestamp: message.timestamp as number,
      };
      dispatchEffect(effect);
      logger.debug({ effect: effect.effect, target: effect.target }, 'Received effect');
      break;
    }

    case 'REACTION_BROADCAST': {
      // Reaction from another player - update state so ReactionOverlay can display it
      const { playerId: currentPlayerId } = get();
      const senderId = message.playerId as string;

      // Don't show our own reactions (we already see them locally)
      if (senderId === currentPlayerId) break;

      set({
        lastReaction: {
          playerId: senderId,
          playerName: message.playerName as string,
          reaction: message.reaction as string,
          timestamp: message.timestamp as number,
        },
      });
      logger.debug(
        { playerId: senderId, reaction: message.reaction },
        'Received reaction broadcast'
      );
      break;
    }

    case 'DEBUG_EVAL': {
      // Execute debug code from gateway (localhost only)
      const evalId = message.evalId as string;
      const code = message.code as string;
      const { socket } = get();
      if (!socket) break;

      logger.info({ evalId, codeLength: code.length }, 'Executing debug eval');

      // Create context with useful objects
      const context = {
        get,
        set,
        store: usePlayerStore,
        logger,
        peerConnection,
        window,
        document,
      };

      (async () => {
        try {
          // Execute the code with context
          const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;
          const fn = new AsyncFunction(
            'get',
            'set',
            'store',
            'logger',
            'peerConnection',
            'window',
            'document',
            `return (async () => { ${code} })()`
          );

          const result = await fn(
            context.get,
            context.set,
            context.store,
            context.logger,
            context.peerConnection,
            context.window,
            context.document
          );

          // Serialize result for JSON
          let serializedResult: unknown;
          try {
            JSON.stringify(result);
            serializedResult = result;
          } catch {
            serializedResult = String(result);
          }

          socket.send(
            JSON.stringify({
              type: 'DEBUG_EVAL_RESPONSE',
              evalId,
              success: true,
              result: serializedResult,
            })
          );
        } catch (error) {
          logger.error({ error, evalId }, 'Debug eval failed');
          socket.send(
            JSON.stringify({
              type: 'DEBUG_EVAL_RESPONSE',
              evalId,
              success: false,
              error: error instanceof Error ? error.message : String(error),
            })
          );
        }
      })();
      break;
    }
  }
}
